// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/LanguageSheet.tsx
// 🔧 PURPOSE: Il foglio con le otto lingue, montato UNA volta a livello app.
//
//             Perché non sta dentro il selettore: il selettore vive nella
//             barra dell'hero (che ha un backdrop-blur: un elemento `fixed`
//             lì dentro resta intrappolato e tagliato) e nel menu utente,
//             che si chiude al primo tocco fuori — smontando il selettore, e
//             con lui il foglio, prima che il tocco sul pulsante della
//             lingua arrivi. Qui il foglio è un portale su <body>, aperto da
//             un evento: sopravvive a qualunque menu si chiuda sotto di lui.
// ============================================

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { APP_LANGUAGES, appLanguageOf, setLanguageManually } from '../lib/locale';
import { setGuestLocale } from '../lib/guestApi';
import { useGuestToken } from '../hooks/useGuestSession';
import { useStatusBarVeil } from '../lib/statusBar';

export const OPEN_LANGUAGE_SHEET_EVENT = 'nva_open_language_sheet';

/** Da chiamare da qualunque pulsante "lingua" dell'app. */
export const openLanguageSheet = (): void => {
  window.dispatchEvent(new CustomEvent(OPEN_LANGUAGE_SHEET_EVENT));
};

const LanguageSheetHost: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = appLanguageOf(i18n.language);
  // Dallo store condiviso della sessione: segue accessi e uscite anche se
  // il foglio resta montato per tutta la vita dell'app.
  const guestToken = useGuestToken();
  // La barra di stato si scurisce come la pagina sotto il velo (lib/statusBar).
  useStatusBarVeil(0.4, open);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_LANGUAGE_SHEET_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_LANGUAGE_SHEET_EVENT, onOpen);
  }, []);

  if (!open || typeof document === 'undefined') return null;

  const close = () => setOpen(false);

  // Con l'ospite dentro, la scelta arriva anche al server: chat, email e
  // push da qui in poi nella lingua nuova.
  const choose = (code: string) => {
    setLanguageManually(code);
    if (guestToken) void setGuestLocale(guestToken, code);
    close();
  };

  return createPortal(
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true" aria-label={t('language.title')}>
      <button type="button" aria-label={t('common.close')} onClick={close} className="absolute inset-0 bg-black/40" />
      <div
        className="absolute left-0 right-0 bottom-0 bg-white dark:bg-gray-900 rounded-t-3xl px-5 pt-4 shadow-2xl"
        style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-900 dark:text-white">{t('language.title')}</h3>
          <button
            type="button"
            onClick={close}
            aria-label={t('common.close')}
            className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {APP_LANGUAGES.map((lang) => {
            const active = lang.code === current;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => choose(lang.code)}
                className={`flex items-center justify-between px-3.5 py-3 rounded-2xl border text-[15px] font-semibold transition-colors ${
                  active
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
                    : 'border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100'
                }`}
              >
                <span>{lang.label}</span>
                {active ? <Check className="h-4 w-4" /> : <span className="text-[11px] font-bold text-gray-400 uppercase">{lang.code}</span>}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{t('language.hint')}</p>
      </div>
    </div>,
    document.body,
  );
};

export default LanguageSheetHost;
